import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { createConsumer } from "@rails/actioncable"

const consumer = createConsumer();

// Mirrors the class swap in app/views/chats/_chat_title.html.erb, so a row
// highlighted here looks like one the server rendered as selected.
const SIDEBAR_SELECTED_CLASS = "bg-gray-50";
const SIDEBAR_UNSELECTED_CLASSES = ["text-gray-700", "hover:text-gray-900", "hover:bg-gray-100"];
// The new row travels over the websocket while this code runs off the POST
// response, so it may not have landed yet — poll briefly rather than give up.
const SIDEBAR_HIGHLIGHT_ATTEMPTS = 10;
const SIDEBAR_HIGHLIGHT_RETRY_MS = 200;

// Page furniture that rides the chat's own stream: the message counters and the
// composer (see app/lib/career_coach/allowance_broadcast.rb). They arrive here
// because they are true of this conversation, but none of them is the model
// answering — handing one to the code below would clear the response timeout,
// and a turn that never came back would look like it landed.
const ALLOWANCE_STREAM_TARGETS = ["chat_message_counter", "chat_composer_notice", "chat_composer"];

export default class extends Controller {
  static values = { chatId: String, signedStreamName: String, }
  static targets = ["messages", "submitButton", "micButton", "questionInput", "messagesScrollarea", "welcomeState", "warning", "error", "chatCount", "weekCount", "chatLimitError", "weekLimitError"]

  // If the AI produces no output within this window we assume the request
  // failed, surface an error and let the user try again.
  RESPONSE_TIMEOUT_MS = 15000
  // How long the "something went wrong" message stays on screen.
  ERROR_VISIBLE_MS = 5000

  connect() {
    if (this.chatIdValue) {
      this.subscribeToChat()
    }
  }

  // Put the message on screen the moment it's sent, rather than waiting for the
  // server to echo it back over the websocket.
  //
  // On a brand-new chat there is no subscription yet — it's opened from the
  // POST response, by which time the server may already have broadcast the user
  // bubble. ActionCable doesn't replay what you missed, so that bubble was
  // simply lost and the chat showed an answer to a question that wasn't there.
  // (The server's 300ms sleep only narrowed that window; it never closed it.)
  //
  // Rendering it here makes the question's appearance independent of socket
  // timing. When the echo does arrive, isDuplicateUserMessage() drops it.
  renderOwnMessage(event) {
    // validateMessage runs first in the action list and stops blank sends.
    if (event.defaultPrevented) return;
    if (!this.hasQuestionInputTarget || !this.hasMessagesTarget) return;

    const content = this.questionInputTarget.value.trim();
    if (!content) return;

    this.removeWelcomeState();

    const bubble = document.createElement("div");
    bubble.className = "message message-user flex justify-end pb-2";

    const body = document.createElement("div");
    body.className = "max-w-[80%] rounded-xl px-4 py-2 bg-gray-50 text-gray-900";
    // textContent, not innerHTML — this is user input.
    body.textContent = content;

    bubble.appendChild(body);
    this.messagesTarget.appendChild(bubble);
    this.scrollToBottom();
  }

  removeWelcomeState() {
    if (this.hasWelcomeStateTarget) {
      this.welcomeStateTarget.remove();
    }
  }

  postMessage(event) {
    const response = event.detail.fetchResponse.response;
    if (response.status === 429) {
      this.handleMessageLimit(response);
      return;
    }

    // The send was accepted, so it will be counted. Done here rather than from
    // the server's echo because on a brand-new chat the socket is not connected
    // yet and that echo can miss; the echo carries an absolute count, so when it
    // does land it corrects this rather than adding to it.
    this.bumpCounters();

    let chatId;
    let signedStreamName;

    // State 1 - No chat initialised
    if (!this.chatIdValue) {
      console.log("📩 postMessage called - No chat initialised");

      chatId = event.detail.fetchResponse.response.headers.get("X-Chat-Id");
      signedStreamName = event.detail.fetchResponse.response.headers.get("X-Signed-Stream-Name");
      // Update the hidden field with the new chat ID
      const chatIdField = document.querySelector('input[name="chat_id"]');

      // Usually already gone — renderOwnMessage clears it on submit — but a
      // send that bypassed that path still needs the welcome state removed.
      this.removeWelcomeState();
      this.revealChatCounter();

      if (chatIdField) {
        chatIdField.value = chatId;
      } else {
        console.warn("⚠️ Could not find chat_id hidden field to update");
      }

      this.highlightChatInSidebar(chatId);
    } else {
      // State 2 - Chat initialised
      chatId = this.chatIdValue;
      signedStreamName = this.signedStreamNameValue;
    }

    if (chatId && signedStreamName) {
      this.chatIdValue = chatId;
      this.signedStreamNameValue = signedStreamName;
      console.log(this.subscription)
      if (!this.subscription) {
        console.log("📩postMessage called - Chat initialised - subscribing to chat");
        this.subscribeToChat();
      }
    }

    // Clear the content field after submission
    const contentField = document.querySelector('textarea[name="content"]');
    if (contentField) {
      contentField.value = '';
    } else {
      console.warn("⚠️ Could not find content field to clear");
    }

    this.scrollToBottom();
  }

  // The server refused the question because the reader is out of messages. Only
  // reachable in a race — the composer is already gone once a limit is reached —
  // so this undoes the bubble renderOwnMessage put up and says which cap it was.
  // The sentences are rendered by the server and hidden (see
  // app/views/chats/_composer.html.erb) so the wording lives in one place.
  handleMessageLimit(response) {
    this.limitReached = true;
    this.removeOwnMessage();

    const reason = response.headers.get("X-Message-Limit");
    const target = reason === "week"
      ? (this.hasWeekLimitErrorTarget ? this.weekLimitErrorTarget : null)
      : (this.hasChatLimitErrorTarget ? this.chatLimitErrorTarget : null);
    if (target) {
      target.classList.remove("hidden");
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
  }

  // The question is still in the textarea — postMessage is what clears it, and a
  // refusal returns before that — so only the optimistic bubble has to go.
  removeOwnMessage() {
    if (!this.hasMessagesTarget) return;

    const bubbles = this.messagesTarget.querySelectorAll(".message-user");
    bubbles[bubbles.length - 1]?.remove();
  }

  // The per-chat counter is rendered hidden while there is no chat to count, so
  // that the turbo-stream target exists before the first question creates one.
  // This is the moment it starts meaning something.
  revealChatCounter() {
    document.getElementById("chat_message_counter")?.classList.remove("hidden");
  }

  bumpCounters() {
    const counters = [
      this.hasChatCountTarget ? this.chatCountTarget : null,
      this.hasWeekCountTarget ? this.weekCountTarget : null,
    ];

    counters.forEach((counter) => {
      if (!counter) return;
      const current = parseInt(counter.textContent.trim(), 10);
      if (Number.isNaN(current)) return;
      counter.textContent = String(current + 1);
    });
  }

  // ChatsController#stream_chat_title broadcasts the new sidebar row on a
  // per-user stream, so it reaches every tab and device and has to arrive
  // unhighlighted. This is the only tab that should show it as open, so it
  // does the highlighting itself.
  highlightChatInSidebar(chatId, attemptsLeft = SIDEBAR_HIGHLIGHT_ATTEMPTS) {
    if (!chatId) return;

    const list = document.getElementById("chats_list_items");
    if (!list) return;

    list.querySelectorAll(`a.${SIDEBAR_SELECTED_CLASS}`).forEach((selected) => {
      selected.classList.remove(SIDEBAR_SELECTED_CLASS);
      selected.classList.add(...SIDEBAR_UNSELECTED_CLASSES);
    });

    const row = document.getElementById(`chat_title_${chatId}`);
    if (!row) {
      if (attemptsLeft > 1) {
        setTimeout(() => this.highlightChatInSidebar(chatId, attemptsLeft - 1), SIDEBAR_HIGHLIGHT_RETRY_MS);
      }
      return;
    }

    row.classList.remove(...SIDEBAR_UNSELECTED_CLASSES);
    row.classList.add(SIDEBAR_SELECTED_CLASS);
  }

  subscribeToChat() {
    if (!this.signedStreamNameValue) {
      console.error("❌ No signed stream name available. Subscription failed.");
      return;
    }

    console.log(`✅ Subscribing to chat stream: ${this.signedStreamNameValue}`);

    this.subscription = consumer.subscriptions.create(
      {
        channel: "Turbo::StreamsChannel",
        signed_stream_name: this.signedStreamNameValue
      },
      {
        received: (data) => {
          console.log("📩 received called");
          if (!data.includes("turbo-stream")) {
            console.warn("⚠️ No turbo-stream tag in data:", data);
            return;
          }

          const fragment = document.createRange().createContextualFragment(data);

          const streamTarget = fragment.querySelector("turbo-stream")?.getAttribute("target");
          if (ALLOWANCE_STREAM_TARGETS.includes(streamTarget)) {
            Turbo.renderStreamMessage(data);
            return;
          }

          // A failing ProcessMessageJob is retried by Sidekiq, and every retry
          // re-broadcasts the same user bubble, so it visually stacks up
          // ("message / message / message" with a gap). Drop the re-broadcast
          // when it duplicates the last unanswered user message. A genuine
          // repeat (after the AI has replied) still gets through.
          if (this.isDuplicateUserMessage(fragment)) {
            return;
          }

          // Any fragment that isn't the echoed user bubble means the AI is
          // actually producing output, so the request didn't time out. Once we
          // see a response we stop caring about the timeout for this message.
          if (!this.isUserMessageFragment(fragment)) {
            this.responseReceived = true;
            this.clearResponseTimeout();
          }

          this.checkForFinalMessage(data);
          this.messagesTarget.append(fragment);
          this.scrollToBottom();
        }
      }
    );
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null;
    }
    this.clearResponseTimeout();
  }

  disableSubmitButton() {
    // A refused send has no response coming, so there is nothing for the timeout
    // to wait on and its "something went wrong" would tell the wrong story. The
    // button stays disabled — handleMessageLimit disabled it.
    if (this.limitReached) {
      this.limitReached = false;
      return;
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
    // The message is now on its way — start watching for a missing response.
    this.startResponseTimeout();
  }

  // The incoming turbo-stream wraps its payload in a <template>; the user bubble
  // is the only fragment that carries the `.message-user` class.
  isUserMessageFragment(fragment) {
    return !!fragment
      .querySelector("template")
      ?.content.querySelector(".message-user");
  }

  startResponseTimeout() {
    this.clearResponseTimeout();
    this.hideError();
    // Fresh turn: no response seen yet for this message.
    this.responseReceived = false;
    this.responseTimeout = setTimeout(() => {
      this.responseTimeout = null;
      // A response slipped in just as the timer fired — leave it alone.
      if (this.responseReceived) {
        return;
      }
      this.cancelRequest();
      this.showError();
      this.enableSubmitButton();
      this.enableMicButton();
    }, this.RESPONSE_TIMEOUT_MS);
  }

  // Tell the backend to stop processing this chat's message so it neither
  // broadcasts a late response nor keeps retrying.
  cancelRequest() {
    if (!this.chatIdValue) {
      return;
    }
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(`/chats/${this.chatIdValue}/cancel`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": token || "",
        "Accept": "application/json",
      },
    }).then((response) => {
      // A non-network failure (e.g. 403/500) resolves rather than rejecting; if
      // the cancel didn't take, the job may keep retrying, so surface it.
      if (!response.ok) {
        console.warn("⚠️ Cancel chat request failed:", response.status);
      }
    }).catch((error) => console.warn("⚠️ Failed to cancel chat request:", error));
  }

  clearResponseTimeout() {
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
      this.responseTimeout = null;
    }
  }

  showError() {
    if (!this.hasErrorTarget) {
      return;
    }
    this.errorTarget.classList.remove("hidden");
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
    this.errorTimeout = setTimeout(() => this.hideError(), this.ERROR_VISIBLE_MS);
  }

  hideError() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }
  }

  // True when the incoming fragment is a user bubble identical to the most
  // recent user bubble that has not yet received an assistant reply. That only
  // happens when the job re-runs and re-broadcasts the same message.
  isDuplicateUserMessage(fragment) {
    const incoming = fragment
      .querySelector("template")
      ?.content.querySelector(".message-user");
    if (!incoming) {
      return false;
    }

    const existingUserMessages = this.messagesTarget.querySelectorAll(".message-user");
    const lastUserMessage = existingUserMessages[existingUserMessages.length - 1];
    if (!lastUserMessage) {
      return false;
    }

    if (lastUserMessage.textContent.trim() !== incoming.textContent.trim()) {
      return false;
    }

    // If an assistant message already follows the last user bubble, the AI has
    // answered and this is a legitimate new turn — let it through.
    let node = lastUserMessage.nextElementSibling;
    while (node) {
      if (node.matches(".message-assistant") || node.hasAttribute("data-msg-final")) {
        return false;
      }
      node = node.nextElementSibling;
    }

    return true;
  }

  checkForFinalMessage(data) {
    if (data.includes('data-msg-final="true"')) {
      this.enableSubmitButton();
    }
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
    }
  }

  enableMicButton() {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.disabled = false;
    }
  }

  // Block sending a blank message and warn the user instead. Wired to the
  // form's `submit` event so it covers both the button and Enter-to-send.
  validateMessage(event) {
    const isBlank = !this.hasQuestionInputTarget ||
      this.questionInputTarget.value.trim().length === 0;

    if (isBlank) {
      event.preventDefault();
      this.showWarning();
    }
  }

  showWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.classList.remove("hidden");
    }
    if (this.hasQuestionInputTarget) {
      this.questionInputTarget.focus();
    }
  }

  hideWarning() {
    if (this.hasWarningTarget) {
      this.warningTarget.classList.add("hidden");
    }
  }

  chatWindowLoaded() {
    // This method is called from an existing chat, and also from "new chat". For existing chats, chatId and signedStream are populated
    // for new chats they are empty.

    // Reset the chat ID value when the chat window is loaded
    this.chatIdValue = "";
    this.signedStreamNameValue = "";

    const chatId = document.querySelector('[data-chat_subscription_chat_id_value]')?.dataset.chat_subscription_chat_id_value;
    const signedStream = document.querySelector('[data-chat_subscription_signed_stream_name_value]')?.dataset.chat_subscription_signed_stream_name_value;

    if (chatId) this.chatIdValue = chatId;
    if (signedStream) this.signedStreamNameValue = signedStream;

    if (this.subscription) {
      console.log("✅ Chat window loaded - unsubscribing from chat");
      this.subscription.unsubscribe()
      this.subscription = null;
    }
    if (this.chatIdValue && this.signedStreamNameValue) {
      console.log("✅ Chat window loaded - subscribing to chat");
      this.subscribeToChat();
    }

    this.scrollToBottom();
  }

  submitOnEnter(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      // Don't submit while a response is in flight (button disabled).
      if (this.hasSubmitButtonTarget && !this.submitButtonTarget.disabled) {
        this.submitButtonTarget.click();
      }
    }
  }

  setQuestion(event) {
    const question = event.currentTarget.dataset.question;
    if (this.hasQuestionInputTarget) {
      this.questionInputTarget.value = question;
      this.questionInputTarget.focus();
      this.hideWarning();
    }
  }

  scrollToBottom() {
    // Not while the welcome state is up: it is taller than the card on a laptop
    // screen, so scrolling it would land the reader below the greeting.
    if (this.hasWelcomeStateTarget) return;

    const messagesContainer = document.querySelector('[data-chat-subscription-target="messages-scrollarea"]');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }
}